import { assertCompleteTask, queryKeys, RAW_ENDPOINTS, SCHEMA_NAMES, sdk } from 'utils/query';
import { useCustomMutation } from 'hooks/api/useCustomMutation';
import {
  ActivationType,
  CreatePrescriptionParameters,
  CreatePrescriptionResponse,
  PrescriptionType,
} from 'models/prescription';
import { displayNotificationError, displayNotificationLoading, displayNotificationSuccess } from 'utils/notifications';
import { t } from 'i18next';
import { PagedResult, rqlBuilder } from '@extrahorizon/javascript-sdk';
import { PrescriptionActivationError } from 'services/errors/PrescriptionActivationError';
import { createFileUrlFromBuffer, showFullName } from 'utils/helpers';
import { sendPrescriptionMail } from 'services/mail';
import { PrescriptionDownloadError } from 'services/errors/PrescriptionDownloadError';
import { downloadFile, FileType } from 'utils/fileManagement';
import retry from 'async-retry';
import { Buffer } from 'buffer';
import { type PrescriptionCreationData, type PrescriptionData, PrescriptionStatus } from '@fibricheck/schema-types';

import { deactivatePrescriptions } from './useDeactivatePrescriptions';

const createPrescription = async ({
  activationType,
  groupId,
  packageId,
  prescriptionType,
  reason,
  language,
  shouldSendEmail,
  emailSubject,
  emailBody,
  emailRecipient,
  patientData,
  deactivationGroupIds,
  usBillingConsent,
  usBillingIcdCode,
}: CreatePrescriptionParameters) => {
  const notificationId = displayNotificationLoading({
    title: t('creating_label'),
    message: t('prescript_loading_label'),
  });

  try {
    const {
      data: {
        data: [prescription],
      },
    } = await sdk.raw.post<PagedResult<CreatePrescriptionResponse>>(RAW_ENDPOINTS.PRESCRIPTIONS, {
      count: 1,
      group_id: groupId,
      package_id: packageId,
      paid_by_group: prescriptionType === PrescriptionType.Free,
      tags: [reason],
    });

    await sdk.data.documents.create<PrescriptionCreationData, PrescriptionData, PrescriptionStatus>('prescriptions', {
      prescriptionId: prescription.id,
      icd10Code: usBillingIcdCode || undefined, // If usBillingIcdCode is an empty string -> turn it into undefined
      rpmBillingConsent: usBillingConsent ?? false,
    });

    if (activationType === ActivationType.Immediate && patientData?.id) {
      const { id: userId } = patientData;

      const now = new Date();

      const result = await sdk.tasks.functions.execute('integration', {
        command: 'activate-prescription',
        data: { hash: prescription.hash, userId },
      });

      if (assertCompleteTask({ result })) {
        await retry(
          async () => {
            const rql = rqlBuilder()
              .eq('data.user.id', userId)
              .in('groupIds', [groupId])
              .eq('data.group.id', groupId)
              .build();

            const patient = await sdk.data.documents.findFirst(SCHEMA_NAMES.physicianDashboardTableRows, {
              rql,
            });

            if (patient && patient.updateTimestamp > now) return;
            throw new PrescriptionActivationError();
          },
          {
            retries: 15,
            maxTimeout: 2000,
          },
        );

        displayNotificationSuccess({
          notificationId,
          message: t('prescript_activated_notification', { patientName: showFullName(patientData) }),
        });
      } else {
        throw new PrescriptionActivationError();
      }
    }

    if (activationType === ActivationType.Deferred) {
      const { data } = await sdk.raw.get(`${RAW_ENDPOINTS.PRESCRIPTIONS}/pdf/${language}?id=${prescription.id}`, {
        responseType: 'arraybuffer',
      });
      if (shouldSendEmail) {
        const recipient = patientData?.email ? patientData?.email : emailRecipient;
        const base64Prescription = Buffer.from(data).toString('base64');
        await sendPrescriptionMail(recipient, prescription.reference, base64Prescription, emailSubject, emailBody);
        displayNotificationSuccess({ notificationId, message: t('email_sent') });
      } else {
        const fileUrl = createFileUrlFromBuffer(data);
        downloadFile(fileUrl, prescription.id, FileType.prescription);
        URL.revokeObjectURL(fileUrl);
        displayNotificationSuccess({ notificationId, message: t('pdf_download_started') });
      }
    }

    if (deactivationGroupIds && deactivationGroupIds.length > 0 && patientData) {
      await deactivatePrescriptions({ groupIds: deactivationGroupIds, patient: patientData });
    }
  } catch {
    displayNotificationError({
      notificationId,
      error:
        activationType === ActivationType.Immediate
          ? new PrescriptionActivationError()
          : new PrescriptionDownloadError(),
    });
  }
};

export const useCreatePrescription = () => {
  return useCustomMutation({
    fn: createPrescription,
    onSuccess: { invalidateQueryKeys: [[queryKeys.patients], [queryKeys.patientData]] },
  });
};
